import { useState } from 'react';

import Alert from '@mui/material/Alert';
import MenuItem from '@mui/material/MenuItem';
import styled from '@mui/material/styles/styled';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { collectionApis } from '../../../apis/collectionApis';
import useToast, { TOAST_TYPE } from '../../../hooks/useToast';
import Text from '../../elements/text/Text';
import Modal from '../../sections/modal/Modal';
import ModalButtons from '../../sections/modal/ModalButtons';

const StyledSpan = styled('span')(({ theme }) => ({
  backgroundColor: theme.palette.action.hover,
  borderBottom: 'solid 2px black',
}));

const DeleteDocsMenuItem = ({ name, onClose }) => {
  const { t } = useTranslation();
  const { projectId, instanceId } = useParams();
  const { showToast } = useToast();

  const [form, setForm] = useState('');
  const [modal, setModal] = useState(false);

  const handleFormChange = (e) => setForm(e.target.value);
  const toggleModal = () => setModal(!modal);

  const handleDeleteDocs = async () => {
    try {
      await collectionApis.deleteAllDocs(projectId, instanceId, name);
      showToast(
        TOAST_TYPE.SUCCESS,
        t('deleteSuccess', {
          ns: 'notifications',
          text: t('collectionDocs', { ns: 'instance' }),
        })
      );
    } catch (error) {
      showToast(
        TOAST_TYPE.ERROR,
        t('deleteError', {
          ns: 'notifications',
          text: t('collectionDocs', { ns: 'instance' }),
        })
      );
    }
  };

  return (
    <>
      <MenuItem sx={{ color: 'error.main' }} onClick={toggleModal}>
        {t('deleteDocs', { ns: 'instance' })}
      </MenuItem>
      <Modal
        openModal={modal}
        title={t('deletion', {
          ns: 'project',
          text: t('collectionDocs', { ns: 'instance' }),
        })}
        handleClose={onClose}
        handleConfirm={handleDeleteDocs}
        action={
          <ModalButtons
            actionProps={{ disabled: form !== name }}
            onAction={handleDeleteDocs}
            onCancel={onClose}
            actionText={t('delete', { ns: 'buttons' })}
            danger
          />
        }
      >
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('cantUndo', { ns: 'descriptions' })}
        </Alert>
        <Text sx={{ mb: 3 }}>
          {t('deleteCollectionDocs', {
            ns: 'descriptions',
            text: name,
          })}{' '}
          <Trans
            i18nKey="deleteValidation"
            ns="descriptions"
            values={{ validation: name }}
            components={[<StyledSpan key="0" />]}
          />
        </Text>

        <TextField
          variant="outlined"
          label="Deletion Confirmation"
          value={form}
          onChange={handleFormChange}
          fullWidth
          size="small"
          slotProps={{
            htmlInput: {
              'aria-label': 'delete confirmation',
            },
          }}
        />
      </Modal>
    </>
  );
};

DeleteDocsMenuItem.propTypes = {
  name: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default DeleteDocsMenuItem;
